import { useState, useEffect, lazy, Suspense } from 'react';
import { FileText, Plus, Edit, Send, Trash2, Mail, Bell } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { API, authFetch, formatDate } from './helpers';

const ReactQuill = lazy(() => import('react-quill-new'));
const quillModules = {
  toolbar: [
    [{ header: [2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['blockquote', 'link'],
    ['clean']
  ]
};

const NewsletterPostsTab = () => {
  const [posts, setPosts] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: '', content: '', excerpt: '', status: 'draft' });
  const [notifyStatus, setNotifyStatus] = useState({});

  const fetchPosts = async () => {
    const res = await authFetch(`${API}/newsletter-posts/all`);
    if (res.ok) setPosts(await res.json());
  };

  useEffect(() => { fetchPosts(); }, []);

  const openNew = () => { setEditing(null); setForm({ title: '', content: '', excerpt: '', status: 'draft' }); setShowForm(true); };
  const openEdit = (post) => { setEditing(post); setForm({ title: post.title, content: post.content, excerpt: post.excerpt || '', status: post.status }); setShowForm(true); };

  const savePost = async (e) => {
    e.preventDefault();
    if (editing) {
      await authFetch(`${API}/newsletter-posts/${editing.id}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(form) });
    } else {
      await authFetch(`${API}/newsletter-posts`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(form) });
    }
    setShowForm(false); setEditing(null); setForm({ title: '', content: '', excerpt: '', status: 'draft' }); fetchPosts();
  };

  const togglePublish = async (post) => {
    const newStatus = post.status === 'published' ? 'draft' : 'published';
    await authFetch(`${API}/newsletter-posts/${post.id}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ status: newStatus }) });
    fetchPosts();
  };

  const deletePost = async (id) => {
    if (!window.confirm('Are you sure you want to delete this post?')) return;
    await authFetch(`${API}/newsletter-posts/${id}`, { method: 'DELETE' });
    fetchPosts();
  };

  const notifySubscribers = async (postId) => {
    setNotifyStatus(prev => ({ ...prev, [postId]: 'sending' }));
    try {
      const res = await authFetch(`${API}/newsletter-posts/${postId}/notify`, { method: 'POST' });
      const data = await res.json();
      setNotifyStatus(prev => ({ ...prev, [postId]: data.status === 'not_configured' ? 'not_configured' : 'sent' }));
      if (data.status === 'not_configured') {
        alert(`Email not configured yet. ${data.subscriber_count} subscribers would receive this.\n\nSetup: Add SENDGRID_API_KEY or RESEND_API_KEY to your backend .env`);
      }
    } catch {
      setNotifyStatus(prev => ({ ...prev, [postId]: 'error' }));
    }
  };

  return (
    <div data-testid="posts-tab">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-brand-dark">Newsletter Posts</h2>
        <button onClick={openNew} className="flex items-center gap-2 bg-brand-gold px-4 py-2 text-sm font-medium text-brand-dark hover:opacity-90" data-testid="create-post-btn">
          <Plus className="w-4 h-4" /> New Post
        </button>
      </div>

      {showForm && (
        <div className="bg-white p-6 mb-6" data-testid="post-form">
          <h3 className="text-lg font-medium text-brand-dark mb-4">{editing ? 'Edit Post' : 'Create New Post'}</h3>
          <form onSubmit={savePost} className="space-y-4">
            <div><Label className="text-brand-dark font-medium">Title</Label><Input required value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} className="mt-1" placeholder="Post title" data-testid="post-title-input" /></div>
            <div><Label className="text-brand-dark font-medium">Excerpt (Short summary for preview)</Label><Input value={form.excerpt} onChange={(e) => setForm({...form, excerpt: e.target.value})} className="mt-1" placeholder="Brief summary" data-testid="post-excerpt-input" /></div>
            <div>
              <Label className="text-brand-dark font-medium">Content</Label>
              <div className="mt-1 bg-white border rounded" data-testid="post-content-editor">
                <Suspense fallback={<div className="p-4 text-gray-400 text-sm">Loading editor...</div>}>
                  <ReactQuill
                    theme="snow"
                    value={form.content}
                    onChange={(val) => setForm({...form, content: val})}
                    modules={quillModules}
                    placeholder="Write your newsletter post..."
                    style={{ minHeight: 250 }}
                  />
                </Suspense>
              </div>
            </div>
            <div>
              <Label className="text-brand-dark font-medium">Status</Label>
              <select value={form.status} onChange={(e) => setForm({...form, status: e.target.value})} className="mt-1 w-full border border-gray-300 p-2 rounded" data-testid="post-status-select">
                <option value="draft">Draft</option><option value="published">Published</option>
              </select>
            </div>
            <div className="flex gap-4">
              <button type="submit" className="bg-brand-gold px-6 py-2 text-sm font-medium text-brand-dark hover:opacity-90" data-testid="save-post-btn">{editing ? 'Update Post' : 'Create Post'}</button>
              <button type="button" onClick={() => { setShowForm(false); setEditing(null); }} className="bg-gray-200 px-6 py-2 text-sm font-medium text-gray-700 hover:bg-gray-300">Cancel</button>
            </div>
          </form>
        </div>
      )}

      {posts.length === 0 ? (
        <div className="text-center py-12 bg-white"><FileText className="w-12 h-12 text-gray-300 mx-auto mb-4" /><p className="text-gray-500">No newsletter posts yet. Create your first post above.</p></div>
      ) : (
        <div className="space-y-3" data-testid="posts-list">
          {posts.map((post) => (
            <div key={post.id} className="bg-white p-5" data-testid={`post-${post.id}`}>
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-1">
                    <h3 className="font-medium text-brand-dark truncate">{post.title}</h3>
                    <span className={`px-2 py-0.5 text-xs font-medium flex-shrink-0 ${post.status === 'published' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>{post.status}</span>
                  </div>
                  {post.excerpt && <p className="text-sm text-gray-500 truncate">{post.excerpt}</p>}
                  <p className="text-xs text-gray-400 mt-1">
                    {post.author_name && `By ${post.author_name} \u2022 `}
                    {post.published_at ? `Published ${formatDate(post.published_at)}` : `Created ${formatDate(post.created_at)}`}
                  </p>
                </div>
                <div className="flex items-center gap-1 ml-4 flex-shrink-0">
                  {post.status === 'published' && (
                    <button
                      onClick={() => notifySubscribers(post.id)}
                      className={`p-2 ${notifyStatus[post.id] === 'sending' ? 'text-yellow-500 animate-pulse' : notifyStatus[post.id] === 'sent' ? 'text-green-500' : 'text-gray-400 hover:text-blue-600'}`}
                      title="Notify subscribers"
                      disabled={notifyStatus[post.id] === 'sending'}
                      data-testid={`notify-post-${post.id}`}
                    >
                      <Bell className="w-4 h-4" />
                    </button>
                  )}
                  <button onClick={() => togglePublish(post)} className={`p-2 ${post.status === 'published' ? 'text-green-600 hover:text-yellow-600' : 'text-gray-400 hover:text-green-600'}`} title={post.status === 'published' ? 'Unpublish' : 'Publish'}>
                    <Send className="w-4 h-4" />
                  </button>
                  <button onClick={() => openEdit(post)} className="p-2 text-gray-400 hover:text-brand-dark" data-testid={`edit-post-${post.id}`}><Edit className="w-4 h-4" /></button>
                  <button onClick={() => deletePost(post.id)} className="p-2 text-gray-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default NewsletterPostsTab;
